<template>
  <v-breadcrumbs 
    :items="processedBreadcrumbs" 
    :class="['breadcrumbs', currentBackgroundClass || 'bg-white-pure', customClass]"
  >
    <template v-slot:prepend>
      <v-icon 
        icon="mdi-home" 
        @click="navigateToHome" 
        class="breadcrumb-home"
        aria-label="Home"
      ></v-icon>
    </template>
    <template v-slot:divider>
      <v-icon icon="mdi-chevron-right" class="breadcrumb-divider"></v-icon>
    </template>
    <template v-slot:item="{ item, index }">
      <v-breadcrumbs-item
        :to="item.to"
        :disabled="item.disabled || (!item.to)"
        :class="{
          'active-breadcrumb': item.active,
          'previous-breadcrumb': index === processedBreadcrumbs.length - 2,
          'breadcrumb-item': true,
          'last-breadcrumb': index === processedBreadcrumbs.length - 1
        }"
        @click.prevent="navigate(item.to)"
      >
        {{ item.title }}
      </v-breadcrumbs-item>
    </template>
  </v-breadcrumbs>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useWhiteTheme } from "@/composables/useWhiteTheme";

const props = defineProps({
  customClass: {
    type: String,
    default: ''
  }
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { currentBackgroundClass } = useWhiteTheme();

// Fetch breadcrumbs from Vuex store
const breadcrumbs = computed(() => store.state.breadcrumbs.breadcrumbs);

// Process breadcrumbs to mark active route
const processedBreadcrumbs = computed(() => {
  return breadcrumbs.value.map((breadcrumb, index) => {
    // Determine if this is the active route
    const isActive = index === breadcrumbs.value.length - 1;
    
    // Return a new object with the active property
    return {
      ...breadcrumb,
      active: isActive,
      disabled: isActive // Disable navigation for the current page
    };
  });
});

// Navigate to home page when home icon is clicked
const navigateToHome = () => {
  router.push({ name: 'Home' });
};

// Handle navigation when a breadcrumb is clicked
const navigate = (to) => {
  if (to) {
    router.push(to);
  }
};
</script>
<style scoped>
.breadcrumbs {
  padding: 4px 0 !important;
  border-radius: 0 !important;
  margin-bottom: 0.5rem !important; /* Reduced from 1rem to match screenshot */
  display: flex !important;
  align-items: center !important;
  box-shadow: none !important;
}

.breadcrumb-home {
  cursor: pointer;
  color: rgb(23, 25, 28);
  margin-right: 4px;
  transition: color 0.2s ease;
  font-size: 18px; /* Size for home icon */
}

.breadcrumb-home:hover {
  color: rgb(0, 82, 189) !important; /* Darker blue color on hover */
}


.breadcrumb-divider {
  color: rgb(122, 124, 126); /* Lighter gray for divider */
  font-size: 16px;
  opacity: 0.7;
}

.breadcrumb-item {
  color: rgb(23, 25, 28); /* Black part color */
  font-weight: 400;
  font-family: proxima-nova, ProximaNova, noto, GESS, GE-SS, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.breadcrumb-item:hover:not(.active-breadcrumb) {
  color: rgb(0, 82, 189) !important; /* Darker blue color on hover */
}

.active-breadcrumb {
  color: rgb(0, 82, 189) !important; /* Darker blue color for active item */
  font-weight: 400;
  pointer-events: none;
}

.previous-breadcrumb:hover {
  text-decoration: none; /* Changed from underline to none to match screenshot */
  opacity: 0.85; /* Subtle hover effect */
}

.last-breadcrumb {
  max-width: 240px; /* Increased for better readability */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Search results page specific styles */
:global(.property-search-results-page) .breadcrumbs {
  background-color: transparent !important;
  box-shadow: none !important;
  padding-left: 0;
  padding-right: 0;
  margin-bottom: 0.5rem;
}

/* Remove after-content from home icon */
:global(.property-search-results-page) .breadcrumb-home::after {
  content: none !important;
}

/* Ensure proper spacing between items */
:global(.v-breadcrumbs-item) {
  padding: 0 2px !important; /* Tighter padding */
}

/* Override Vuetify default breadcrumb styling */
:deep(.v-breadcrumbs__item) {
  font-family: proxima-nova, ProximaNova, noto, GESS, GE-SS, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  padding: 0 2px;
}

:deep(.v-breadcrumbs__divider) {
  padding: 0 4px;
}

/* Ensure breadcrumb container has proper spacing */
.breadcrumbs-container {
  padding: 4px 0 !important;
  margin-top: 0 !important;
  margin-bottom: 8px !important;
}
</style>